
import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2 } from 'lucide-react'

interface TemplateFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
}

interface Subetapa {
  codigo: string
  nome: string
  itens: any[]
}

interface Etapa {
  codigo: string
  nome: string
  subetapas: Subetapa[]
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    categoria: '',
    bdi: 0,
    ativo: true
  })

  const [etapas, setEtapas] = useState<Etapa[]>([])

  useEffect(() => {
    if (initialData) {
      setFormData({
        nome: initialData.nome || '',
        descricao: initialData.descricao || '',
        categoria: initialData.categoria || '',
        bdi: initialData.bdi || 0,
        ativo: initialData.ativo !== undefined ? initialData.ativo : true
      })
      setEtapas(initialData.etapas || [])
    } else {
      setFormData({
        nome: '',
        descricao: '',
        categoria: '',
        bdi: 0,
        ativo: true
      })
      setEtapas([])
    }
  }, [initialData, isOpen])

  const addEtapa = () => {
    const newEtapa: Etapa = {
      codigo: (etapas.length + 1).toString(),
      nome: `Etapa ${etapas.length + 1}`,
      subetapas: []
    }
    setEtapas([...etapas, newEtapa])
  }

  const addSubetapa = (etapaIndex: number) => {
    const newSubetapa: Subetapa = {
      codigo: `${etapas[etapaIndex].codigo}.${etapas[etapaIndex].subetapas.length + 1}`,
      nome: `Subetapa ${etapas[etapaIndex].subetapas.length + 1}`,
      itens: []
    }
    
    const newEtapas = [...etapas]
    newEtapas[etapaIndex].subetapas.push(newSubetapa)
    setEtapas(newEtapas)
  }

  const removeEtapa = (etapaIndex: number) => {
    const newEtapas = etapas.filter((_, i) => i !== etapaIndex)
    setEtapas(newEtapas)
  }

  const removeSubetapa = (etapaIndex: number, subetapaIndex: number) => {
    const newEtapas = [...etapas]
    newEtapas[etapaIndex].subetapas.splice(subetapaIndex, 1)
    setEtapas(newEtapas)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const submitData = {
      ...formData,
      etapas
    }

    await onSubmit(submitData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informações Básicas */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Informações do Template</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Template *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecionar categoria...</option>
                  <option value="Residencial">Residencial</option>
                  <option value="Comercial">Comercial</option>
                  <option value="Industrial">Industrial</option>
                  <option value="Infraestrutura">Infraestrutura</option>
                  <option value="Reforma">Reforma</option>
                  <option value="Construção">Construção</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BDI Padrão (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.bdi}
                  onChange={(e) => setFormData(prev => ({ ...prev, bdi: Number(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="ativo" className="text-sm font-medium text-gray-700">
                  Template ativo
                </label>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <textarea
                value={formData.descricao}
                onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Estrutura de Etapas */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Estrutura de Etapas</h3>
              <button
                type="button"
                onClick={addEtapa}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus size={16} />
                Adicionar Etapa
              </button>
            </div>

            {etapas.map((etapa, etapaIndex) => (
              <div key={etapaIndex} className="bg-white p-4 rounded-lg mb-4">
                <div className="flex items-center gap-4 mb-4">
                  <input
                    type="text"
                    value={etapa.codigo}
                    onChange={(e) => {
                      const newEtapas = [...etapas]
                      newEtapas[etapaIndex].codigo = e.target.value
                      setEtapas(newEtapas)
                    }}
                    placeholder="Código"
                    className="w-20 p-2 border border-gray-300 rounded-md"
                  />
                  <input
                    type="text"
                    value={etapa.nome}
                    onChange={(e) => {
                      const newEtapas = [...etapas]
                      newEtapas[etapaIndex].nome = e.target.value
                      setEtapas(newEtapas)
                    }}
                    placeholder="Nome da etapa"
                    className="flex-1 p-2 border border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => addSubetapa(etapaIndex)}
                    className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700"
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeEtapa(etapaIndex)}
                    className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Subetapas */}
                {etapa.subetapas.map((subetapa, subetapaIndex) => (
                  <div key={subetapaIndex} className="bg-gray-50 p-3 rounded-lg mb-3 ml-4">
                    <div className="flex items-center gap-4">
                      <input
                        type="text"
                        value={subetapa.codigo}
                        onChange={(e) => {
                          const newEtapas = [...etapas]
                          newEtapas[etapaIndex].subetapas[subetapaIndex].codigo = e.target.value
                          setEtapas(newEtapas)
                        }}
                        placeholder="Código"
                        className="w-20 p-2 border border-gray-300 rounded-md"
                      />
                      <input
                        type="text"
                        value={subetapa.nome}
                        onChange={(e) => {
                          const newEtapas = [...etapas]
                          newEtapas[etapaIndex].subetapas[subetapaIndex].nome = e.target.value
                          setEtapas(newEtapas)
                        }}
                        placeholder="Nome da subetapa"
                        className="flex-1 p-2 border border-gray-300 rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeSubetapa(etapaIndex, subetapaIndex)}
                        className="bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {etapas.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>Nenhuma etapa adicionada ainda</p>
                <p className="text-sm">Clique em "Adicionar Etapa" para começar</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {initialData ? 'Atualizar' : 'Criar'} Template
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TemplateForm
